import { CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { capitalizeAll, ChartDateRange, ChartSettings, getLanguageByCode, getTimeSpanResolutionInMs, MultiSeriesDataPoint, TimeSeriesChartPanelProps,
    TimeSpan, XYChart, XYChartType, TimeSeriesChartPanelWithCurrentValue, TimeSeriesChartPanelWithCurrentValueProps, GenericMessageBox }
    from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { Agent, EntityMetricsDefinition, NumericMetricInfo, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import moment from 'moment';
import React, { CSSProperties, useContext, useMemo } from 'react';
import { GridSizeSettings } from '..';
import { LazyComp } from '../..';
import { DEFAULT_LANGUAGE, NO_VALUE, BasicXYChart, SUPPORTED_LANGUAGES } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { createChartExportSettings, getPreferredLanguage, ReadonlyNonEmptyArray } from '../../../utils/utils';
import { MetricPreviewData, KPIStatus } from '../utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            flexDirection: 'column',

            '& $chartPanel': {
                height: 300,
            },
        },

        chartPanel: {
            '& .chart': {
                height: '100%',
            },
            '&:not(:last-child)': {
                marginBottom: theme.spacing(2),
            },
        },

        chartPanelContent: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            flex         : 1,
            height       : '100%',
        },

        chart: {
            flex  : 1,
            height: '100%',
        },

        currentValue: {
            '&.override': {
                '& .value': {
                    color     : theme.palette.text.primary,
                    fontSize  : 20,
                    fontWeight: 'bold',
                },
                '& .unit': {
                    color     : theme.palette.text.secondary,
                    fontSize  : 20,
                    fontWeight: 'bold',
                    marginLeft: theme.spacing(0.5),
                },

                '&.status-good': {
                    '& .value': {
                        color: theme.palette.success.main,
                    },
                    '& .unit': {
                        color: theme.palette.success.main,
                    },
                },
                '&.status-bad': {
                    '& .value': {
                        color: theme.palette.error.main,
                    },
                    '& .unit': {
                        color: theme.palette.error.main,
                    },
                },
            },
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },

        [theme.breakpoints.up('md')]: {
            root: {
                '& $chartPanel': {
                    height: 350,
                },
            },
            currentValue: {
                '&.override': {
                    '& .value': {
                        fontSize: 25,
                    },
                    '& .unit': {
                        color     : theme.palette.text.secondary,
                        fontSize  : 25,
                        marginLeft: theme.spacing(1),
                    },
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            root: {
                '& $chartPanel': {
                    height: 400,
                },
            },
            currentValue: {
                '&.override': {
                    '& .value': {
                        fontSize: 30,
                    },
                    '& .unit': {
                        fontSize: 30,
                    },
                },
            },
        },
    };
}, { name: 'KPIMetricsComparisonViewContainer', index: 1 });



/**
 * A helper function to create a new metric name from the given metric's actual name and an index.
 * This component display multiple metrics for comparison however, these are the same metrics (same name, display name, etc...), just different
 * time interval. We need to make them different metrics therefore we have to append something to their name to make each of them unique.
 * Solution is to add the timestamp to the metric's name. So if we are comparing a metric call Temperature and we want to compare the temperature
 * data between today and yesterday, we will create 2 new metrics with these unique name, temperature_today, temperature_yesterday,etc...
 */
const createMetricName = (metricName: string, timestamp: number): string => {
    return `${metricName}_${timestamp}`;
};



export interface KPIMetricsComparisonViewContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;
    readonly metricDefinition: EntityMetricsDefinition;
    readonly metric: NumericMetricInfo;
    readonly chartIdPrefix: string;

    // OPTIONAL aggregation to use. We will use 'avg' by default if this is not provided
    readonly aggregation?: TimeSeriesAggregation | undefined;

    // The starting times of the data we are trying to compare
    // For example: 10/01/2021, 10/02/2021, 10/03/2021 mean we want to compare the metric data for starting on these 3 days. NOTE that this
    // DOES NOT mean we will compare 3 days of data. It just mean we will start on these 3 days. How many days/months/year of data we will be
    // comparing will depend on the timeSpan.
    readonly timestamps: ReadonlyNonEmptyArray<number>;

    // The length of the compare time e.g. 1 day, 1 week, 1 month, etc... We will need this to calculate the endTimes of the data
    readonly timeSpan: TimeSpan;

    // The data to be displayed
    readonly multiIntervalMetricsData: readonly MetricPreviewData[][] | undefined;

    // To specify a different chart type to use for ALL metrics shown in this component instead of using the default chart type, AREA.
    readonly chartType?: BasicXYChart | undefined;

    readonly customPrimaryActions?: TimeSeriesChartPanelProps['customPrimaryActions'] | undefined;
    readonly customSecondaryActions?: TimeSeriesChartPanelProps['customSecondaryActions'] | undefined;

    readonly gridSizes?: GridSizeSettings | undefined;
}



/**
 * This component is used for allowing the user to compare metrics from a collection on different time interval.
 *
 * This component display all numeric metrics specified in the props. Each metric will be displayed in a separate chart.
 * Each chart will display multiple series and each series show data for the same metric but different time interval.
 * For example, 1 chart can show many series for Temperature data. 1 series can show temperature data for this month, another series
 * show temperature data for the previous month, and another series show data for the month before that.
 */
export const KPIMetricsComparisonViewContainer: React.FC<KPIMetricsComparisonViewContainerProps> = (
    props: KPIMetricsComparisonViewContainerProps,
) => {

    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);

    const project = useSelectProject(props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get current selected language in the uiState
    const selectedLanguage = getLanguageByCode(
        SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || DEFAULT_LANGUAGE;

    // Get the agent's preferred language and figure out the language that is being used in the UI based on the agent's preferred language and the
    // current language in the uiState. If the agent is logged in and has preferred language set than use the agent's preferred language.
    // Else use the current selected language in the UI state.
    const currentLanguage = loggedInAgent
        ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE)
        : selectedLanguage;

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    /**
     * props.multiIntervalMetricsData contain multiple metrics data for multiple timestamps. We only need data for the specified
     * metricDefinitionId/metricName. So we need to go though props.multiIntervalMetricsData and filter out all other metrics.
     */
    const multiIntervalMetricData = useMemo(() => {
        if (props.multiIntervalMetricsData) {
            return props.multiIntervalMetricsData.map((oneIntervalMetricData) => {
                // Find the kpiMetricData which has metricDefinitionId and metricName matched the metricDefinitionId and metric name in props
                // That kpiMetricData mst be the metric data for the metric we need to show in this component
                return oneIntervalMetricData.find((kpiMetricData) => {
                    return kpiMetricData.metricDefinition.metricsDefinitionId === props.metricDefinition.metricsDefinitionId
                    && kpiMetricData.metric.name === props.metric.name;
                });
            });
        }
        return undefined;
    }, [props.multiIntervalMetricsData, props.metricDefinition.metricsDefinitionId, props.metric.name]);



    /**
     * Get the metric's currentValue and status from multiIntervalMetricData
     */
    const currentValueAndStatus = useMemo((): {
        readonly value: number | undefined;
        readonly status: KPIStatus;
    } | undefined => {
        if (multiIntervalMetricData && multiIntervalMetricData.length > 0 && multiIntervalMetricData[0] !== undefined) {
            // Use the FIRST data from the multiIntervalMetricsData to create 'currentValueAndStatus' because it is the data for the first timestamp
            return {
                value : multiIntervalMetricData[0].currentValue,
                status: multiIntervalMetricData[0].status,
            };
        }
        return undefined;
    }, [multiIntervalMetricData]);


    /**
     * Convert the data from multiIntervalMetricData into a format that can be used by the chart
     */
    const multiMetricsDataPoints = useMemo((): {
        readonly dataPoints: readonly(readonly MultiSeriesDataPoint<number>[])[];
        readonly dateRanges: readonly ChartDateRange[];
    } | undefined => {
        if (multiIntervalMetricData) {
            /**
             * The data points in 'multiIntervalMetricData' uses the actual metric's name e.g.
             *      dataPoints = [{
             *          date: 'Oct', values: {temperature:5, pressure:10}
             *      }, {
             *          date: 'Nov', values: {temperature:5, pressure:10}
             *      }]
             *
             * However, to compare the metrics, we have to give them fake names that is a combination of metricName + timestamp like this
             *      dataPoints = [{
             *          date: 'Oct', values: {temperature_oct:5, pressure_oct:10}
             *      }, {
             *          date: 'Nov', values: {temperature_nov:5, pressure_nov:10}
             *      }]
             */
            const reformattedDataPoints = props.timestamps.map((timestamp, index) => {
                const actualMetricName = props.metric.name;
                const newMetricName = createMetricName(actualMetricName, timestamp);

                // Convert the dataPoints to use newMetricName
                return multiIntervalMetricData[index]?.dataPoints.map((dataPoint): MultiSeriesDataPoint<number> => {
                    return {
                        date      : dataPoint.date,
                        dateValue : dataPoint.dateValue,
                        dateString: dataPoint.dateString,
                        values    : {
                            [newMetricName]: dataPoint.values[actualMetricName],
                        },
                    };
                }) ?? [];
            });

            /**
             * IMPORTANT NOTE: When we create a chart to compare multiple set of data on a different interval e.g. data for a month Jan
             * compare to data for Feb, we need to make sure their X axes are aligned. If 1 X axis has longer interval than another than
             * the data points will not line up. This is a problem with MONTH and YEAR time spans because a month can have 28 days, 29 days,
             * 30 days, etc... and some year can have 365 days and some has 366 days. When we create X Axes for each of the data set, we
             * need to make sure they have the same interval therefore, we need to find the longest interval between these data set and
             * use that interval for the x axes.
             */
            const maxInterval = props.timestamps.reduce((result, timestamp) => {
                const startTime = moment(timestamp).startOf(props.timeSpan).valueOf();
                const endTime = moment(timestamp).startOf(props.timeSpan).endOf(props.timeSpan).valueOf();
                return Math.max(result, endTime - startTime);
            }, Number.MIN_SAFE_INTEGER);


            return {
                dataPoints: reformattedDataPoints,
                dateRanges: props.timestamps.map((timestamp) => {
                    const startTime = moment(timestamp).startOf(props.timeSpan).valueOf();
                    return {
                        start     : startTime,
                        end       : startTime + maxInterval,
                        resolution: getTimeSpanResolutionInMs(props.timeSpan),
                    };
                }),
            };
        }

        return undefined;
    }, [multiIntervalMetricData, props.metric.name, props.timeSpan, props.timestamps]);



    /**
     * Because we are given 1 metric info but we need to fetch and display multiple data series, one for each timestamp, we will have to
     * set up some fake Metric objects which are basically copies of the provided metric so that we can pass them to the chart. The Chart
     * needs 1 Metric object per line.
     *
     * Let's say we have 2 timestamps and the the metric is Temperature, we will create this
     *      chartableMetrics: [{
     *          // The copies of the originalMetric with modified name and displayName
     *          name: 'temperature_1',
     *          ...
     *      }, {
     *          // The copies of the originalMetric with modified name and displayName
     *          name: 'temperature_2',
     *          ...
     *      }]
     */
    const chartableMetrics = useMemo(() => {
        return props.timestamps.map((timestamp): NumericMetricInfo => {
            /**
             * We need to change the metric's name because it must be unique. We will add some # to the end of the metric's name
             *
             * As for displayName, we will use the timestamp + timeSpan. This displayName will be used for chart legends and mouse
             * over so if we keep the original name e.g. Temperature, it will not be helpful because all of the metrics displayed
             * in the same chart are for the same metric so they all show Temperature.
             * So instead of using the original metric's displayName, we show the date. However we will not display the full date in
             * every case, it will depend on the timeSpan. If the timeSpan is DAY then we show the full date. But if the timeSpan is
             * YEAR and we are comparing data between 2 weeks, we will show something like '2021' and '2020'. And if
             * we are comparing data between 2 months, we will show 'Nov 2021' and 'Dec 2021', etc...
             */
            const displayName = (() => {
                if (props.timeSpan === TimeSpan.HOUR) {
                    return new Intl.DateTimeFormat(currentLanguage.locale2, {
                        month: 'short', day: '2-digit', year: 'numeric', hour12: true, minute: '2-digit', second: '2-digit',
                    }).format(timestamp);
                }
                if (props.timeSpan === TimeSpan.MONTH) {
                    return new Intl.DateTimeFormat(currentLanguage.locale2, {
                        month: 'short', year: 'numeric',
                    }).format(timestamp);
                }
                if (props.timeSpan === TimeSpan.YEAR) {
                    return new Intl.DateTimeFormat(currentLanguage.locale2, {
                        year: 'numeric',
                    }).format(timestamp);
                }

                // Show 'MMM DD, YYYY' for the other timeSpans, DAY and WEEK
                return new Intl.DateTimeFormat(currentLanguage.locale2, {
                    month: 'short', day: '2-digit', year: 'numeric',
                }).format(timestamp);
            })();
            return {
                ...props.metric,
                name: createMetricName(props.metric.name, timestamp),
                displayName,
            };
        });

    }, [currentLanguage.locale2, props.metric, props.timeSpan, props.timestamps]);


    /**
     * Create the chart settings and use useMemo to cache it
     */
    const chartSettings = useMemo((): ChartSettings => {
        return {
            className: clsx(classes.chart),
            locale   : currentLanguage.locale2,
        };
    }, [classes.chart, currentLanguage.locale2]);


    const dataLength = useMemo(() => {
        return multiMetricsDataPoints?.dataPoints?.reduce((length, data) => {
            return Math.max(length, data.length);
        }, 0);
    }, [multiMetricsDataPoints]);


    const currentValueSettings = useMemo((): TimeSeriesChartPanelWithCurrentValueProps['currentValue'] => {
        return {
            value: currentValueAndStatus?.value !== undefined
                ? trans('{{value, number-format}}', { value: currentValueAndStatus.value }) : NO_VALUE,
            unit     : props.metric.unit,
            className: clsx(classes.currentValue, 'override', `status-${currentValueAndStatus?.status.toLowerCase()}`),
        };
    }, [classes.currentValue, currentValueAndStatus, props.metric.unit, trans]);


    /**
     * Create the export options for the Chart and cache it. We will pass this to the Chart settings later
     */
    const chartExportSettings = useMemo((): ChartSettings['exportSettings'] => {
        return createChartExportSettings(trans);
    }, [trans]);


    return (
        <Grid
            item
            className={clsx(props.className, classes.root)}
            style={props.style}
            xs={props.gridSizes?.xs ?? 12}
            sm={props.gridSizes?.sm}
            md={props.gridSizes?.md}
            lg={props.gridSizes?.lg}
            xl={props.gridSizes?.xl}
        >
            {project && chartableMetrics ? (
                <TimeSeriesChartPanelWithCurrentValue
                    className={classes.chartPanel}
                    title={props.metric.displayName || props.metric.name}
                    icon="show_chart"
                    borderlessHeader
                    dataCount={dataLength}
                    seriesCount={chartableMetrics.length}
                    customPrimaryActions={props.customPrimaryActions}
                    customSecondaryActions={props.customSecondaryActions}
                    noDataMessageComp={(
                        <GenericMessageBox
                            className={classes.noDataMessage}
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                            })}
                        />
                    )}
                    currentValue={currentValueSettings}
                >
                    <div className={classes.chartPanelContent}>
                        <LazyComp>
                            <XYChart
                                {...chartSettings}
                                chartType={props.chartType ?? XYChartType.AREA}
                                showLegends
                                shareXAxis={false}
                                shareYAxis
                                showYAxes
                                chartId={`${props.chartIdPrefix}-${props.metric.name}`}
                                dataPoints={multiMetricsDataPoints?.dataPoints || []}
                                displayMetrics={chartableMetrics}
                                dateRange={multiMetricsDataPoints?.dateRanges}
                                exportSettings={chartExportSettings}
                            />
                        </LazyComp>
                    </div>
                </TimeSeriesChartPanelWithCurrentValue>
            ) : (
                <CircularProgress />
            )}
        </Grid>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('KPIMetricsComparisonViewContainer', KPIMetricsComparisonViewContainer);
